import {
  Alert,
  App,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs from 'dayjs';
import {
  notificationsApi,
  type CreatedDevice,
  type GatewayDevice,
} from '@/api/notifications';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

export function GatewayDevicesPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('NOTIFICATIONS:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('NOTIFICATIONS:EDIT'));
  const canDelete = useAuthStore((s) => s.hasAuthority('NOTIFICATIONS:DELETE'));

  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<GatewayDevice | null>(null);
  const [revealed, setRevealed] = useState<CreatedDevice | null>(null);
  const [createForm] = Form.useForm<{ deviceName: string }>();
  const [renameForm] = Form.useForm<{ deviceName: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'devices'],
    queryFn: () => notificationsApi.listDevices(false),
    refetchInterval: 15_000,
  });

  const create = useMutation({
    mutationFn: (v: { deviceName: string }) => notificationsApi.createDevice(v),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['notifications', 'devices'] });
      setCreating(false);
      createForm.resetFields();
      setRevealed(d);
    },
    onError: (e) => message.error(extractError(e)),
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      notificationsApi.renameDevice(id, { deviceName: name }),
    onSuccess: () => {
      message.success('Device renamed');
      qc.invalidateQueries({ queryKey: ['notifications', 'devices'] });
      setRenaming(null);
      renameForm.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const setActive = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) =>
      notificationsApi.setDeviceActive(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', 'devices'] }),
    onError: (e) => message.error(extractError(e)),
  });

  const rotate = useMutation({
    mutationFn: (id: number) => notificationsApi.rotateDeviceToken(id),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['notifications', 'devices'] });
      setRevealed(d);
    },
    onError: (e) => message.error(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => notificationsApi.deleteDevice(id),
    onSuccess: () => {
      message.success('Device removed');
      qc.invalidateQueries({ queryKey: ['notifications', 'devices'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const copyToken = () => {
    if (!revealed) return;
    navigator.clipboard.writeText(revealed.token).then(
      () => message.success('Token copied to clipboard'),
      () => message.error('Could not copy — copy manually'),
    );
  };

  const columns: TableProps<GatewayDevice>['columns'] = [
    {
      title: 'Device',
      dataIndex: 'deviceName',
      render: (n: string) => <Typography.Text strong>{n}</Typography.Text>,
    },
    {
      title: 'Status',
      dataIndex: 'active',
      width: 110,
      render: (a: boolean, row) =>
        canEdit ? (
          <Switch
            size="small"
            checked={a}
            onChange={(v) => setActive.mutate({ id: row.id, value: v })}
          />
        ) : (
          <Tag color={a ? 'success' : 'default'}>{a ? 'Active' : 'Inactive'}</Tag>
        ),
    },
    {
      title: 'Last seen',
      dataIndex: 'lastSeenAt',
      width: 180,
      render: (v: string | null) => {
        if (!v) return <Typography.Text type="secondary">Never</Typography.Text>;
        const ago = dayjs().diff(dayjs(v), 'minute');
        const stale = ago > 5;
        return (
          <Tooltip title={dayjs(v).format('D MMM YYYY HH:mm:ss')}>
            <Typography.Text type={stale ? 'warning' : undefined}>
              {ago < 1 ? 'just now' : `${ago} min ago`}
            </Typography.Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Battery',
      dataIndex: 'lastBattery',
      width: 90,
      align: 'center',
      render: (n: number | null) => (n == null ? '—' : `${n}%`),
    },
    {
      title: 'Signal',
      dataIndex: 'lastSignal',
      width: 90,
      align: 'center',
      render: (n: number | null) => (n == null ? '—' : `${n}/4`),
    },
    {
      title: 'SIM balance',
      dataIndex: 'lastSimBalance',
      width: 140,
      render: (s: string | null) => s ?? '—',
    },
    {
      title: 'Actions',
      width: 260,
      align: 'right',
      render: (_, row) => (
        <Space size="small">
          {canEdit && (
            <Button
              size="small"
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setRenaming(row);
                renameForm.setFieldsValue({ deviceName: row.deviceName });
              }}
            >
              Rename
            </Button>
          )}
          {canEdit && (
            <Popconfirm
              title="Rotate token?"
              description="The old token will stop working immediately. The new token is shown once."
              onConfirm={() => rotate.mutate(row.id)}
              okText="Rotate"
            >
              <Button size="small" type="text" icon={<ReloadOutlined />}>
                Rotate
              </Button>
            </Popconfirm>
          )}
          {canDelete && (
            <Popconfirm
              title="Remove this device?"
              description="The device's token will be invalidated."
              onConfirm={() => remove.mutate(row.id)}
              okText="Remove"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Notifications"
        title="Gateway devices"
        description="Phones registered to send SMS on the school's behalf. The bearer token is shown once at create / rotate time — copy it then."
        count={{ value: data?.length ?? 0, label: 'devices' }}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
              Register device
            </Button>
          ) : null
        }
      />

      <Table
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={data ?? []}
        pagination={false}
      />

      <Modal
        title="Register a new gateway device"
        open={creating}
        onCancel={() => setCreating(false)}
        onOk={() => createForm.submit()}
        okText="Register"
        confirmLoading={create.isPending}
        destroyOnHidden
      >
        {creating && (
          <Form form={createForm} layout="vertical" onFinish={(v) => create.mutate(v)}>
            <Form.Item
              label="Device name"
              name="deviceName"
              tooltip="Human-friendly label, e.g. 'Reception Phone' or 'Owner Backup SIM'."
              rules={[{ required: true, max: 100 }]}
            >
              <Input autoFocus placeholder="Reception Phone" />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title={renaming ? `Rename ${renaming.deviceName}` : ''}
        open={!!renaming}
        onCancel={() => setRenaming(null)}
        onOk={() => renameForm.submit()}
        okText="Save"
        confirmLoading={rename.isPending}
        destroyOnHidden
      >
        {renaming && (
          <Form
            form={renameForm}
            layout="vertical"
            onFinish={(v) => rename.mutate({ id: renaming.id, name: v.deviceName })}
          >
            <Form.Item label="Device name" name="deviceName" rules={[{ required: true, max: 100 }]}>
              <Input autoFocus />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title="Device token"
        open={!!revealed}
        onCancel={() => setRevealed(null)}
        footer={[
          <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={copyToken}>
            Copy token
          </Button>,
          <Button key="close" onClick={() => setRevealed(null)}>
            Done
          </Button>,
        ]}
        destroyOnHidden
        width={560}
      >
        {revealed && (
          <div style={{ display: 'grid', gap: 14 }}>
            <Alert
              type="warning"
              showIcon
              message="Save this token now"
              description="It will not be shown again. If you lose it, rotate the token to issue a new one."
            />
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Device
              </Typography.Text>
              <div>
                <Typography.Text strong>{revealed.device.deviceName}</Typography.Text>
              </div>
            </div>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Token (paste into the gateway app as <code>X-Device-Token</code>)
              </Typography.Text>
              <Input.TextArea
                value={revealed.token}
                readOnly
                rows={3}
                style={{ fontFamily: 'monospace', marginTop: 4 }}
                onFocus={(e) => e.currentTarget.select()}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
